import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useEffect, useState } from "react";
import { FaDownload } from 'react-icons/fa';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getInvoices } from "../../../../api/action/order";
import { Loader } from "../../../../components/Loader";
import BackButton from "../components/CustomFields/BackButton";
import FormatByOperatingLocation from "../../../../components/FormatByOperatingLocation";
import { academyLogger } from "utils/logger";

// InvoiceView Component
const InvoiceView = () => {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.companyReducer.company);
  const [invoice, setInvoice] = useState(null);
  const navigate = useNavigate();
  const userDetail = useSelector((state) => state?.auth?.data);
  const auth = useSelector((state) => state?.auth?.isAuthenticated);

  useEffect(() => {
    if (!auth) {
      navigate("/sign-in");
      return;
    }
    const fetchInvoice = async () => {
      try {
        const userId = userDetail?.primaryId;
        if (!userId) {
          academyLogger.error("User ID not found.");
          return;
        }

        const response = await dispatch(getInvoices(userId));
        if (response && response.length > 0) {
          setInvoice(response[0]);
        } else {
          academyLogger.error("No invoice found.");
        }
      } catch (error) {
        academyLogger.error("Error fetching invoice:", error);
      }
    };

    fetchInvoice();
  }, [dispatch, auth, navigate, userDetail?.primaryId]);

  // Generate PDF from the invoice
  const generatePDF = () => {
    const content = document.getElementById("invoiceContent");
    if (!content) return;

    html2canvas(content, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Invoice_${invoice.primaryId}.pdf`);
    });
  };

  if (!invoice) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  // Dynamic data
  const companyName = invoice.order?.company?.name || "Velammal CBSE Schools";
  const applicantName = `${invoice.user?.firstName || ""} ${invoice.user?.lastName || ""}`.trim() || "John Doe";
  const orderStatus = invoice.order?.currentStatus || "N/A";
  const createdDate = new Date(invoice.order?.createdDate).toLocaleDateString() || "N/A";
  const companyLogo = company?.logoImageContent?.displayLink || null;
  
  // Get user mobile number with phone formatting
  const userMobile = invoice.user?.mobileNumber || invoice.user?.phoneNumber || "";
  
  // Get items from invoice order
  const orderItems = invoice.order?.items || [];
  
  // Calculate total amount
  const totalAmount = orderItems.reduce((sum, item) => {
    const itemTotal = (item.quantity || 0) * (item.price || 0);
    return sum + itemTotal;
  }, 0);

  return (
    <>
      <br />
      <div style={styles.invoiceContainer}>
        <div className="absolute top-20 left-4 z-20" style={{ paddingTop: '30px' }}>
          <BackButton />
        </div>
        <h1 style={styles.heading}>Invoice</h1>

        {companyLogo && (
          <div className="text-center mb-8">
            <img
              src={companyLogo}
              alt="Company Logo"
              className="w-40 mx-auto mb-4"
            />
          </div>
        )}
        
        <button onClick={generatePDF} style={styles.downloadButton}>
          <FaDownload style={{ marginRight: "8px" }} />
          Download PDF
        </button>
        
        {/* Company Name */}
        <div style={styles.companyName}>{companyName}</div>
        <br />
        
        <div style={{ ...styles.invoiceNumber, textAlign: 'right' }}>
          <strong>Invoice Number: </strong>{invoice.primaryId || "N/A"}
        </div>
        <br />

        {/* Invoice Information */}
        <div style={styles.invoiceContent} id="invoiceContent">
          {/* Company, Applicant, Status, Date Information */}
          <div style={styles.labelValueContainer}>
            <span style={styles.label}>Applicant:</span>
            <span style={styles.value}>{applicantName}</span>
          </div>
          
          <div style={styles.labelValueContainer}>
            <span style={styles.label}>Mobile Number:</span>
            <span style={styles.value}>
              <FormatByOperatingLocation value={userMobile} type="PHONE" />
            </span>
          </div>
          
          <div style={styles.labelValueContainer}>
            <span style={styles.label}>Status:</span>
            <span style={styles.value}>{orderStatus}</span>
          </div>
          
          <div style={styles.labelValueContainer}>
            <span style={styles.label}>Date:</span>
            <span style={styles.value}>{createdDate}</span>
          </div>

          {/* Address - You might want to fetch this from user data */}
          <div style={styles.labelValueContainer}>
            <span style={styles.label}>Address:</span>
            <span style={styles.value}>
              {invoice.user?.address || "123 Main St, City, State, Zipcode: 12345"}
            </span>
          </div>

          <br />

          {/* Items Table */}
          <div>
            <h3 style={{ fontSize: "1.2rem", fontWeight: "500", marginBottom: "10px" }}>Items</h3>
            <table style={styles.invoiceItems}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Description</th>
                  <th style={styles.tableHeader}>Quantity</th>
                  <th style={styles.tableHeader}>Price</th>
                  <th style={styles.tableHeader}>Total</th>
                </tr>
              </thead>
              <tbody>
                {orderItems.length > 0 ? (
                  orderItems.map((item, index) => (
                    <tr key={index}>
                      <td style={styles.tableCell}>{item.description || item.name || "Item"}</td>
                      <td style={styles.tableCell}>{item.quantity || 1}</td>
                      <td style={styles.tableCell}>
                        <FormatByOperatingLocation value={item.price || 0} type="CURRENCY" />
                      </td>
                      <td style={styles.tableCell}>
                        <FormatByOperatingLocation value={(item.quantity || 1) * (item.price || 0)} type="CURRENCY" />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td style={styles.tableCell} colSpan="4" className="text-center">
                      Ist STD
                    </td>
                    <td style={styles.tableCell}>1</td>
                    <td style={styles.tableCell}>
                      <FormatByOperatingLocation value={200} type="CURRENCY" />
                    </td>
                    <td style={styles.tableCell}>
                      <FormatByOperatingLocation value={200} type="CURRENCY" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Total Amount */}
          <div style={styles.totalAmount}>
            <h3>
              Total Amount: <FormatByOperatingLocation value={totalAmount || 200} type="CURRENCY" />
            </h3>
          </div>
        </div>
      </div>
      <br />
    </>
  );
};

export default InvoiceView;

const styles = {
  invoiceContainer: {
    padding: "40px 30px",
    fontFamily: "Roboto, sans-serif",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    maxWidth: "900px",
    margin: "0 auto",
  },
  heading: {
    fontSize: "2.5rem",
    fontWeight: "600",
    color: "#333",
    margin: 0,
  },
  invoiceNumber: {
    fontSize: "1.2rem",
    color: "#333",
    marginTop: "10px",
  },
  companyName: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    color: "#333",
    textAlign: "center",
    marginTop: "10px",
  },
  downloadButton: {
    padding: "12px 25px",
    backgroundColor: "#4caf50",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "1rem",
    transition: "background-color 0.3s ease",
    marginTop: "20px",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
  },
  invoiceContent: {
    marginTop: "20px",
  },
  labelValueContainer: {
    display: "flex",
    justifyContent: "flex-start",
    marginBottom: "10px",
    alignItems: "center",
  },
  label: {
    fontWeight: "600",
    fontSize: "1rem",
    color: "#333",
    width: "30%",
    margin: 0,
  },
  value: {
    fontSize: "1rem",
    color: "#555",
    width: "70%",
    margin: 0,
  },
  invoiceItems: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "20px",
    marginTop: "20px",
  },
  tableHeader: {
    backgroundColor: "#f4f4f4",
    textAlign: "left",
    padding: "12px 15px",
    fontSize: "1rem",
    fontWeight: "500",
    borderBottom: "1px solid #ddd",
  },
  tableCell: {
    borderBottom: "1px solid #ddd",
    padding: "12px 15px",
    fontSize: "1rem",
  },
  totalAmount: {
    padding: "20px",
    backgroundColor: "#f4f4f4",
    borderRadius: "8px",
    fontWeight: "600",
    fontSize: "1.2rem",
    textAlign: "right",
    marginTop: "20px",
  },
};